import { useMemo, useState } from 'react';
import { overlapping } from '../lib/format';
import type { EnrichedResource, Reservation } from '../lib/types';

/**
 * Give an existing booking a resource, or take one away.
 *
 * Bookings come in from the source list without equipment — raised
 * against a project first, resourced later — so this is the step that
 * completes them. It doubles as the fix for a mis-assignment, which
 * beats cancelling and re-raising the booking.
 *
 * The conflict list is advisory, exactly as it is when booking from
 * scratch: it warns and still lets you save.
 */
export function AssignModal({
  reservation,
  resources,
  reservations,
  saving,
  onAssign,
  onClose,
}: {
  reservation: Reservation;
  /** Bookable resources only — the API applies the same rule. */
  resources: EnrichedResource[];
  reservations: Reservation[];
  saving: boolean;
  onAssign: (reservation: Reservation, equipment: string) => void;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState(reservation.equipment);

  const equipment = useMemo(
    () =>
      resources
        .filter((r) => r.type === 'Equipment')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  );
  const personnel = useMemo(
    () =>
      resources
        .filter((r) => r.type === 'Personnel')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  );

  // Ignore this booking itself when looking for clashes, or reassigning
  // to the same resource would always report a conflict with itself.
  const conflicts = useMemo(
    () =>
      overlapping(
        reservations.filter((r) => r.id !== reservation.id),
        choice,
        reservation.startDate,
        reservation.endDate,
      ),
    [reservations, choice, reservation],
  );

  const changed = choice !== reservation.equipment;

  return (
    <div className="modal-overlay is-nested" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <span
            style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}
          >
            {reservation.equipment ? 'Reassign booking' : 'Assign a resource'}
          </span>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div
          style={{
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px',
            marginBottom: 14,
          }}
        >
          <div
            style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}
          >
            {reservation.projectNumber} – {reservation.projectName}
          </div>
          <div
            style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}
          >
            {reservation.reservedBy} · {reservation.startDate} →{' '}
            {reservation.endDate}
          </div>
        </div>

        <label className="form-label">Resource</label>
        <select
          className="form-input"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          <option value="">— Unassigned —</option>
          <optgroup label="── Equipment ──">
            {equipment.map((r) => (
              <option key={r.title} value={r.title}>
                {r.name} ({r.title})
              </option>
            ))}
          </optgroup>
          <optgroup label="── Personnel ──">
            {personnel.map((r) => (
              <option key={r.title} value={r.title}>
                {r.name} — {r.category}
              </option>
            ))}
          </optgroup>
        </select>

        {conflicts.length > 0 && (
          <div className="conflict-box" style={{ marginTop: 10 }}>
            <p style={{ fontWeight: 500 }}>Conflict detected</p>
            {conflicts.map((c) => (
              <p key={c.id} style={{ marginTop: 3 }}>
                {c.projectNumber} – {c.projectName} · {c.startDate} → {c.endDate}
              </p>
            ))}
          </div>
        )}

        <div className="form-actions" style={{ marginTop: 12 }}>
          <button
            className="btn-save"
            disabled={saving || !changed}
            onClick={() => onAssign(reservation, choice)}
          >
            {saving
              ? 'Saving…'
              : choice === ''
                ? 'Unassign'
                : 'Assign resource'}
          </button>
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
